import { useState } from 'react';
import { useVideo } from '../context/VideoContext';
import { motion } from 'framer-motion';
import { Tab } from '@headlessui/react';
import { BookmarkIcon, HeartIcon } from '@heroicons/react/24/solid';

const ProfilePage = () => {
  const { likedVideos, bookmarkedVideos, videos } = useVideo();
  const [selectedTab, setSelectedTab] = useState(0);

  // User profile information (sample data)
  const userProfile = {
    username: "username123",
    name: "Vishal Kumar",
    bio: "",
    avatar: "https://randomuser.me/api/portraits/men/42.jpg",
    followers: 15689,
    following: 324,
    verified: true,
    stats: {
      likes: likedVideos ? Object.keys(likedVideos).length : 0,
      bookmarks: bookmarkedVideos ? Object.keys(bookmarkedVideos).length : 0,
      totalViews: 4829036
    }
  };

  // Get liked and bookmarked videos
  const likedVideosList = videos.filter(video => likedVideos && likedVideos[video.id]);
  const bookmarkedVideosList = videos.filter(video => bookmarkedVideos && bookmarkedVideos[video.id]);
  
  // Sample activity data
  const recentActivity = [
    { type: 'like', content: 'liked a video', time: '2 hours ago' },
    { type: 'comment', content: 'commented: "Love this!"', time: '1 day ago' },
    { type: 'bookmark', content: 'saved a video', time: '3 days ago' }
  ];

  return (
    <div className="min-h-screen bg-black pt-4 pb-20">
      <div className="px-4 max-w-md mx-auto">
        {/* Profile Header */}
        <div className="flex items-center mb-6">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative"
          >
            <img 
              src={userProfile.avatar} 
              alt={userProfile.name} 
              className="w-20 h-20 rounded-full border-2 border-pink-500"
            />
            {userProfile.verified && (
              <span className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center border-2 border-black">
                ✓
              </span>
            )}
          </motion.div>
          
          <div className="ml-4 flex-1">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-white">{userProfile.name}</h1>
              {userProfile.verified && (
                <span className="ml-1 text-blue-500 text-sm">✓</span>
              )}
            </div>
            <p className="text-zinc-400 text-sm">@{userProfile.username}</p>
            
            {/* <div className="flex mt-2 space-x-4 text-sm">
              <span className="text-white"><strong>{userProfile.followers.toLocaleString()}</strong> <span className="text-zinc-400">followers</span></span>
              <span className="text-white"><strong>{userProfile.following.toLocaleString()}</strong> <span className="text-zinc-400">following</span></span>
            </div> */}
          </div>
          
          <button className="px-4 py-1.5 bg-pink-500 text-white font-medium rounded-full text-sm">
            Edit
          </button>
        </div>
        
        {/* Bio */}
        <p className="text-zinc-300 mb-6">{userProfile.bio}</p>
        
        
        {/* Tabs */}
        <Tab.Group onChange={setSelectedTab} selectedIndex={selectedTab}>
          <Tab.List className="flex border-b border-zinc-800 mb-6">
            <Tab className={`flex-1 py-3 text-sm font-medium outline-none ${selectedTab === 0 ? 'text-pink-500 border-b-2 border-pink-500' : 'text-zinc-400'}`}>
              Liked
            </Tab>
            <Tab className={`flex-1 py-3 text-sm font-medium outline-none ${selectedTab === 1 ? 'text-pink-500 border-b-2 border-pink-500' : 'text-zinc-400'}`}>
              Saved
            </Tab>
            <Tab className={`flex-1 py-3 text-sm font-medium outline-none ${selectedTab === 2 ? 'text-pink-500 border-b-2 border-pink-500' : 'text-zinc-400'}`}>
              Activity
            </Tab>
          </Tab.List>
          
          <Tab.Panels>
            {/* Liked Videos */}
            <Tab.Panel>
              {likedVideosList.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {likedVideosList.map(video => (
                    <div key={video.id} className="relative aspect-[9/16] rounded-lg overflow-hidden">
                      <img 
                        src={video.thumbnail || video.src} 
                        alt={video.description} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 right-1">
                        <HeartIcon className="h-4 w-4 text-pink-500" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <HeartIcon className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-400">No liked videos yet</p>
                </div>
              )}
            </Tab.Panel>
            
            {/* Saved Videos */}
            <Tab.Panel>
              {bookmarkedVideosList.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {bookmarkedVideosList.map(video => (
                    <div key={video.id} className="relative aspect-[9/16] rounded-lg overflow-hidden">
                      <img 
                        src={video.thumbnail || video.src} 
                        alt={video.description} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 right-1">
                        <BookmarkIcon className="h-4 w-4 text-yellow-500" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <BookmarkIcon className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-400">No saved videos yet</p>
                </div>
              )}
            </Tab.Panel>
            
            {/* Activity */}
            <Tab.Panel>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start bg-zinc-900 rounded-lg p-3">
                    <div className="bg-zinc-800 rounded-full p-2 mr-3">
                      {activity.type === 'like' && <HeartIcon className="h-5 w-5 text-pink-500" />}
                      {activity.type === 'bookmark' && <BookmarkIcon className="h-5 w-5 text-yellow-500" />}
                      {activity.type === 'comment' && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-white">You {activity.content}</p>
                      <p className="text-xs text-zinc-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default ProfilePage; 